import { useEffect, useRef, useState } from 'react';
import { PreviewCard } from '@base-ui/react/preview-card';
import { useTranslation } from 'react-i18next';
import { TrackBar } from './TrackBar';
import type { CellTone, IColumnDepRef, IMatrixColumn } from './progressMatrix';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './headerDependencies.module.css';

/** Tone for a dependency's progress mini-bar: done → complete, started → at-risk,
 *  untouched → blocked (a 0% predecessor is what blocks a successor). */
function percentTone(percent: number): CellTone {
  if (percent >= 100) return 'complete';
  if (percent > 0) return 'at-risk';
  return 'blocked';
}

function DepGroup({
  title, dir, refs, onJump,
}: {
  title: string;
  dir: 'pred' | 'succ';
  refs: IColumnDepRef[];
  /** Jump the grid to this dependency's column (see MatrixTable). Rows render
   *  as plain text when absent. The name rides along for the miss prompts. */
  onJump?: (taskId: string, taskName: string) => void;
}) {
  const { t } = useTranslation();
  if (refs.length === 0) return null;
  return (
    <div className={styles.group}>
      <div className={`${styles.groupTitle} ${dir === 'pred' ? styles.pred : styles.succ}`}>
        <span className={styles.groupArrow}>{dir === 'pred' ? '◂' : '▸'}</span>
        {title}
        <span className={styles.groupCount}>{refs.length}</span>
      </div>
      {refs.map((r) => {
        const content = (
          <>
            <span className={styles.rowName}>{r.name}</span>
            {!r.committed && <span className={styles.rowTag}>{t('activities.deps.uncommitted')}</span>}
            <span className={styles.rowRight}>
              <TrackBar percent={r.progressPercent} tone={percentTone(r.progressPercent)} className={styles.mini} />
              <span className={styles.rowPct}>{r.progressPercent}%</span>
            </span>
          </>
        );
        // An uncommitted neighbour has no column at any level to jump to.
        if (!onJump || !r.committed) {
          return <div key={r.id} className={styles.row}>{content}</div>;
        }
        return (
          <button
            key={r.id}
            type="button"
            className={`${styles.row} ${styles.rowJumpable}`}
            title={r.name}
            onClick={() => onJump(r.id, r.name)}
          >
            {content}
            <span className={styles.rowJumpArrow} aria-hidden="true">→</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * §2-A + §2-D as one component: the inline predecessor/successor count chips
 * (◂N amber upstream, N▸ blue downstream) that, on hover, reveal a preview card
 * listing each dependency with its live % and a colour-coded mini-bar. Renders
 * nothing when the activity has no dependencies.
 */
export function HeaderDependencies({
  column,
  onJumpToTask,
}: {
  column: IMatrixColumn;
  /** Scroll the grid to a dependency's column and flash it (MatrixTable's
   *  jump). Makes the card's dependency rows clickable when provided. */
  onJumpToTask?: (taskId: string, taskName: string) => void;
}) {
  const { t } = useTranslation();
  // PreviewCard.Root mounts on first hover/focus (armed) instead of one closed
  // instance per column header (437 columns). Once armed it STAYS mounted: the
  // trigger element is then stable, so Escape/blur can close the card without
  // unmounting the element that holds keyboard focus.
  const [armed, setArmed] = useState(false);
  const [open, setOpen] = useState(false);
  // Arming via keyboard focus unmounts the focused placeholder span, which drops
  // DOM focus to <body>. Restore it onto the real trigger so a Tabbing user keeps
  // their place (focusing the trigger also fires Base UI's useFocus — same open
  // behavior as an always-mounted trigger).
  const armedByFocusRef = useRef(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  // The close is scheduled by OUR pointer handlers, not left to Base UI's hover
  // tracking: the Root mounts UNDER an already-hovering pointer, so Base UI
  // never sees the pointerenter its leave-to-close tracking hangs off — with
  // `defaultOpen` alone the card stayed open after the mouse left the chips.
  const closeTimerRef = useRef<number | undefined>(undefined);
  const cancelClose = () => {
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = undefined;
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = undefined;
      setOpen(false);
    }, 80);
  };
  useEffect(() => () => window.clearTimeout(closeTimerRef.current), []);
  useEffect(() => {
    if (armed && armedByFocusRef.current) {
      armedByFocusRef.current = false;
      triggerRef.current?.focus();
    }
  }, [armed]);
  // Safety net for fast sweeps across several chips: arming opens on the
  // PLACEHOLDER's pointerenter, and a quick pointer can exit the chip before
  // the real trigger mounts in its place — the trigger then never receives the
  // pointerleave that would close it, orphaning the card open. While a
  // pointer-opened card is showing:
  //  - any scroll closes it outright — wheeling the grid slides chips under a
  //    STATIONARY pointer, which opens cards through Chrome's post-scroll hover
  //    update without a single true pointer event ever crossing out of them;
  //  - moves whose target is outside both the chips and the card start the 80ms
  //    close countdown (grace to cross the sideOffset gap into the card, which
  //    cancels it). The countdown deliberately does NOT reset on further outside
  //    moves — a long sweep must not keep postponing the close until the mouse
  //    stops. Chrome's hover updates arrive as synthetic MOUSE moves (the
  //    pointer never physically moved), hence listening to both event families.
  // Focus-opened cards are exempt from both — a keyboard user nudging the mouse
  // or scrolling shouldn't dismiss the card they tabbed to.
  const openedByPointerRef = useRef(false);
  const popupRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onMove = (e: MouseEvent) => {
      if (!openedByPointerRef.current) return;
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popupRef.current?.contains(target)) {
        cancelClose();
        return;
      }
      if (closeTimerRef.current === undefined) scheduleClose();
    };
    const onScroll = (event: Event) => {
      if (!openedByPointerRef.current) return;
      // Scrolling the card's OWN list (it max-heights and overflows) must not
      // dismiss it — only outside scrolls count, i.e. the grid sliding the
      // chips away from under the pointer.
      if (popupRef.current?.contains(event.target as Node)) return;
      cancelClose();
      setOpen(false);
    };
    document.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('scroll', onScroll, { capture: true });
    };
    // The handlers only touch stable refs/setters, so the closures staying
    // from the render that opened the card is fine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const preds = column.predecessors ?? [];
  const succs = column.successors ?? [];
  // Clicking a dependency row is a navigation: close the card immediately and
  // hand off to the grid's jump (scroll + flash) — keeping it open would leave
  // it floating over the column the user just asked to look at.
  const jump = onJumpToTask
    ? (taskId: string, taskName: string) => {
        cancelClose();
        setOpen(false);
        onJumpToTask(taskId, taskName);
      }
    : undefined;
  if (preds.length === 0 && succs.length === 0) return null;

  const chips = (
    <>
      {preds.length > 0 && (
        <span className={`${styles.chip} ${styles.chipPred}`}>
          <span className={styles.chipArrow}>◂</span>{preds.length}
        </span>
      )}
      {succs.length > 0 && (
        <span className={`${styles.chip} ${styles.chipSucc}`}>
          {succs.length}<span className={styles.chipArrow}>▸</span>
        </span>
      )}
    </>
  );

  if (!armed) {
    return (
      <span
        className={styles.chips}
        tabIndex={0}
        onPointerEnter={() => {
          openedByPointerRef.current = true;
          setArmed(true);
          setOpen(true);
        }}
        onFocus={() => {
          armedByFocusRef.current = true;
          openedByPointerRef.current = false;
          setArmed(true);
          setOpen(true);
        }}
      >
        {chips}
      </span>
    );
  }

  return (
    <PreviewCard.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) cancelClose();
        setOpen(nextOpen);
      }}
    >
      <PreviewCard.Trigger
        render={<span ref={triggerRef} />}
        className={styles.chips}
        tabIndex={0}
        delay={0}
        closeDelay={80}
        onPointerEnter={() => {
          openedByPointerRef.current = true;
          cancelClose();
          setOpen(true);
        }}
        onPointerLeave={scheduleClose}
        onFocus={() => {
          // Same reset the placeholder's onFocus does: Tabbing onto the
          // already-armed trigger re-opens via Base UI's focus handling, and
          // without clearing a stale pointer flag from an earlier hover, the
          // document-level dismissal net would treat the keyboard user's card
          // as pointer-opened and close it on any incidental move or scroll.
          openedByPointerRef.current = false;
        }}
      >
        {chips}
      </PreviewCard.Trigger>
      <PreviewCard.Portal>
        <PreviewCard.Positioner sideOffset={6} align="center" collisionPadding={8}>
          {/* Hovering the card itself keeps it open (the mini-bars are meant to
              be read); leaving either the chips or the card closes it. */}
          <PreviewCard.Popup ref={popupRef} className={styles.card} onPointerEnter={cancelClose} onPointerLeave={scheduleClose}>
            <ScrollArea className={styles.cardScroll}>
              <div className={styles.cardHead}>{column.name}</div>
              <DepGroup title={t('activities.tracker.deps.predecessors')} dir="pred" refs={preds} onJump={jump} />
              <DepGroup title={t('activities.tracker.deps.successors')} dir="succ" refs={succs} onJump={jump} />
            </ScrollArea>
          </PreviewCard.Popup>
        </PreviewCard.Positioner>
      </PreviewCard.Portal>
    </PreviewCard.Root>
  );
}
